import { useState, useEffect } from 'react'
import { reportesAPI, ventasAPI, cajaAPI, fmt, fmtFecha, fmtHora } from '../services/api'
import { useAuth } from '../context/AuthContext'
import Boleta from '../components/Boleta'
import toast from 'react-hot-toast'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

// Fechas en hora LOCAL (Chile), no UTC — evita que "Hoy" salte al día siguiente por la tarde
const hoyISO = () => new Date().toLocaleDateString('en-CA')
const diasAtras = n => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toLocaleDateString('en-CA')
}
// Acepta coma o punto como separador decimal (importante para Chile)
const num = v => parseFloat(String(v ?? '').replace(',', '.')) || 0

// Nombres amigables de los tipos de documento
const TIPO_DOC = {
  boleta: 'Boletas', factura: 'Facturas', nota_venta: 'Tickets',
  nota_credito: 'Notas de crédito', nota_debito: 'Notas de débito',
  cotizacion: 'Cotizaciones', guia_despacho: 'Guías de despacho',
}
const nombreDoc = tipo => TIPO_DOC[tipo] || tipo
const COLORES_DONA = ['#4A7DFC', '#34C38F', '#F5A623', '#8B5CF6', '#E5484D', '#17375E']
const CONCEPTO_FONDO = 'Efectivo para la siguiente apertura'

// Cierra el modal al hacer clic en el fondo, pero NO cuando el usuario arrastra
// el mouse para seleccionar texto dentro de un campo y suelta fuera.
// Se usa onMouseDown (no onClick) porque el "click" se dispara al SOLTAR: si el
// arrastre empezó dentro del input y terminó sobre el fondo, el navegador lo
// reportaba como un clic en el fondo y la ventana se cerraba sola.
// e.target === e.currentTarget garantiza que el gesto nació en el fondo mismo.
const cerrarSiEsFondo = (accion) => (e) => {
  if (e.target === e.currentTarget) accion()
}

// ── Gráfico de dona (SVG puro, sin librerías) ──
function Dona({ data }) {
  const total = data.reduce((s, d) => s + d.n, 0)
  if (!total) return null
  const R = 56, C = 2 * Math.PI * R
  let offset = 0
  return (
    <svg width="150" height="150" viewBox="0 0 150 150" style={{flexShrink:0}}>
      <g transform="rotate(-90 75 75)">
        <circle cx="75" cy="75" r={R} fill="none" stroke="var(--color-border)" strokeWidth="20"/>
        {data.map((d, i) => {
          const dash = (d.n / total) * C
          const el = (
            <circle key={i} cx="75" cy="75" r={R} fill="none" stroke={d.color}
              strokeWidth="20" strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-offset}/>
          )
          offset += dash
          return el
        })}
      </g>
      <text x="75" y="72" textAnchor="middle" fontSize="26" fontWeight="700" fill="var(--color-text)">{total}</text>
      <text x="75" y="92" textAnchor="middle" fontSize="11" fill="var(--color-muted)">Total</text>
    </svg>
  )
}

export default function Arqueo() {
  const { usuario } = useAuth()
  const esAdmin = usuario?.rol === 'admin' || usuario?.rol === 'superadmin'

  const [desde, setDesde]   = useState(hoyISO())
  const [hasta, setHasta]   = useState(hoyISO())
  const [turnos, setTurnos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [cajeroFiltro, setCajeroFiltro] = useState('')
  const [selId, setSelId]   = useState(null)

  // Corrección de cierre (solo admin)
  const [corrigiendo, setCorrigiendo] = useState(null)
  const [guardando, setGuardando]     = useState(false)
  const [formCorr, setFormCorr]       = useState({})

  // Modal "Ver documentos" del turno
  const [docsModal, setDocsModal]         = useState(null)   // turno seleccionado
  const [docsLista, setDocsLista]         = useState([])
  const [cargandoDocs, setCargandoDocs]   = useState(false)

  // Reimpresión de boleta (admin, desde Ver documentos)
  const [reimpresion, setReimpresion]     = useState(null)
  const [cargandoReimp, setCargandoReimp] = useState(null)
  const [generandoPdf, setGenerandoPdf]   = useState(false)

  useEffect(() => { cargar() }, [desde, hasta])

  const cargar = async () => {
    setCargando(true)
    try {
      const { data } = await reportesAPI.arqueo({ desde, hasta })
      setTurnos(data.turnos || [])
    } catch {
      setTurnos([])
    } finally { setCargando(false) }
  }

  const rangoRapido = (d, h) => { setDesde(d); setHasta(h) }
  const esRango = (d, h) => desde === d && hasta === h

  // Cajeros presentes en los resultados (para el filtro)
  const cajeros = [...new Map(turnos.map(t => [t.usuario_id, t.cajero])).entries()]
  const visibles = cajeroFiltro ? turnos.filter(t => t.usuario_id === cajeroFiltro) : turnos

  // Cierre destacado: el que eligió el admin, o el más reciente
  const sel = visibles.find(t => t.id === selId) || visibles[0] || null

  // ── Helpers de totales por cierre ──
  const otrosTotal   = t => (t.otros_medios || []).reduce((s, m) => s + m.total, 0)
  const totTeorico   = t => t.efectivo_teorico + t.debito_teorico + t.credito_teorico + t.transferencia_teorico + otrosTotal(t)
  const difTotal     = t => (t.efectivo_dif || 0) + (t.debito_dif || 0) + (t.credito_dif || 0) + (t.transferencia_dif || 0)
  const totEntregado = t => totTeorico(t) + difTotal(t)
  const nDocs        = t => (t.documentos || []).reduce((s, d) => s + d.n, 0)

  const fmtDif = d => d === null || d === undefined ? '—' : (d > 0 ? '+' : '') + fmt(d)
  // Saldo positivo o cuadrado → verde · Falta dinero (negativo) → rojo
  const colDif = d => d === null || d === undefined ? 'var(--color-muted)'
    : d < 0 ? 'var(--color-danger)' : 'var(--color-success)'

  // ── Corrección ──
  const abrirCorreccion = t => {
    setFormCorr({
      efectivo:      t.efectivo_real      == null ? '' : String(t.efectivo_real),
      debito:        t.debito_real        == null ? '' : String(t.debito_real),
      credito:       t.credito_real       == null ? '' : String(t.credito_real),
      transferencia: t.transferencia_real == null ? '' : String(t.transferencia_real),
      fondo:         t.fondo_siguiente    == null ? '' : String(t.fondo_siguiente),
      observacion:   '',
    })
    setCorrigiendo(t)
  }

  const guardarCorreccion = async () => {
    if (formCorr.efectivo === '') { toast.error('Indica el efectivo contado'); return }
    setGuardando(true)
    try {
      await cajaAPI.corregirCierre(corrigiendo.id, {
        monto_final_real:   num(formCorr.efectivo),
        debito_real:        formCorr.debito        === '' ? null : num(formCorr.debito),
        credito_real:       formCorr.credito       === '' ? null : num(formCorr.credito),
        transferencia_real: formCorr.transferencia === '' ? null : num(formCorr.transferencia),
        fondo_siguiente:    num(formCorr.fondo),
        observacion:        formCorr.observacion || null,
      })
      toast.success('Cierre corregido')
      setCorrigiendo(null)
      cargar()
    } catch {} finally { setGuardando(false) }
  }

  // ── Ver documentos del turno ──
  const verDocumentos = async (t) => {
    setDocsModal(t)
    setCargandoDocs(true)
    try {
      const { data } = await ventasAPI.listar({ turno_id: t.id, limit: 200 })
      setDocsLista(data.data || [])
    } catch { setDocsLista([]) }
    finally { setCargandoDocs(false) }
  }

  // ── Reimpresión (desde Ver documentos) ──
  const reimprimir = async (v) => {
    setCargandoReimp(v.id)
    try {
      const { data } = await ventasAPI.obtener(v.id)
      setReimpresion({
        documento: data.documento,
        items: (data.detalles || []).map(d => ({
          cantidad: parseFloat(d.cantidad),
          descripcion: d.descripcion,
          precio_unitario: parseFloat(d.precio_unitario),
        })),
        pago: { nombre: data.pagos?.[0]?.medio_pago || '—' },
        vuelto: 0,
      })
    } catch { toast.error('No se pudo cargar el documento') }
    finally { setCargandoReimp(null) }
  }

  const descargarPdfReimp = async () => {
    setGenerandoPdf(true)
    try {
      const elemento = document.getElementById('boleta-imprimir')
      const canvas = await html2canvas(elemento, { scale: 3, backgroundColor: '#ffffff' })
      const imgData = canvas.toDataURL('image/png')
      const anchoMm = 80
      const altoMm = (canvas.height * anchoMm) / canvas.width
      const pdf = new jsPDF({ unit: 'mm', format: [anchoMm, altoMm] })
      pdf.addImage(imgData, 'PNG', 0, 0, anchoMm, altoMm)
      const folio = reimpresion?.documento?.folio || reimpresion?.documento?.id?.slice(-6).toUpperCase()
      pdf.save(`boleta-${folio}.pdf`)
    } catch { toast.error('Error al generar el PDF') }
    finally { setGenerandoPdf(false) }
  }

  // ── Exportar informe (CSV para Excel) ──
  const exportarInforme = () => {
    if (visibles.length === 0) { toast.error('No hay cierres para exportar'); return }
    const filas = [
      ['Fecha', 'Caja', 'Vendedor', 'Total teorico', 'Total entregado', 'Diferencia', 'Documentos'],
      ...visibles.map(t => [
        fmtFecha(t.cierre_en), t.caja_nombre, t.cajero,
        Math.round(totTeorico(t)), Math.round(totEntregado(t)), Math.round(difTotal(t)), nDocs(t),
      ]),
    ]
    // BOM + punto y coma: para que Excel en Chile lo abra directo en columnas
    const csv = '\uFEFF' + filas.map(f => f.join(';')).join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `cierres-caja-${desde}-a-${hasta}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  // ── Datos derivados del cierre destacado ──
  const donaData = sel ? (sel.documentos || []).map((d, i) => ({
    label: nombreDoc(d.tipo), n: d.n, total: d.total, color: COLORES_DONA[i % COLORES_DONA.length],
  })) : []
  const totalDocsSel = donaData.reduce((s, d) => s + d.n, 0)
  const totalMontoSel = donaData.reduce((s, d) => s + (d.total || 0), 0)
  const movsSel      = sel ? (sel.movimientos || []) : []
  const ingresosSel  = movsSel.filter(m => m.tipo === 'ingreso').reduce((s, m) => s + m.monto, 0)
  const egresosSel   = movsSel.filter(m => m.tipo === 'egreso' && m.concepto !== CONCEPTO_FONDO).reduce((s, m) => s + m.monto, 0)
  const movsSinFondo = movsSel.filter(m => m.concepto !== CONCEPTO_FONDO)
  const difSel       = sel ? difTotal(sel) : 0

  const FilaEfectivo = ({ label, valor, signo }) => (
    <div style={{display:'flex', justifyContent:'space-between', fontSize:13, padding:'3px 0'}}>
      <span style={{color:'var(--color-muted)'}}>{label}</span>
      <span style={{fontWeight:500}}>{signo}{fmt(valor)}</span>
    </div>
  )

  return (
    <div style={{padding:24, display:'flex', flexDirection:'column', gap:16}}>

      {/* Encabezado */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12}}>
        <div>
          <div className="section-title" style={{marginBottom:2}}>Cierres de caja</div>
          <div style={{fontSize:13, color:'var(--color-muted)'}}>
            Lo que el sistema calculó vs lo que entregó cada vendedor
          </div>
        </div>
        <button className="btn btn-secondary" onClick={exportarInforme}>⬇ Exportar informe</button>
      </div>

      {/* Filtros */}
      <div className="card" style={{display:'flex', alignItems:'flex-end', gap:12, flexWrap:'wrap'}}>
        <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
          <button className={`btn ${esRango(hoyISO(), hoyISO()) ? 'btn-primary' : 'btn-secondary'}`}
            onClick={()=>rangoRapido(hoyISO(), hoyISO())}>Hoy</button>
          <button className={`btn ${esRango(diasAtras(1), diasAtras(1)) ? 'btn-primary' : 'btn-secondary'}`}
            onClick={()=>rangoRapido(diasAtras(1), diasAtras(1))}>Ayer</button>
          <button className={`btn ${esRango(diasAtras(6), hoyISO()) ? 'btn-primary' : 'btn-secondary'}`}
            onClick={()=>rangoRapido(diasAtras(6), hoyISO())}>Últimos 7 días</button>
          <button className={`btn ${esRango(diasAtras(29), hoyISO()) ? 'btn-primary' : 'btn-secondary'}`}
            onClick={()=>rangoRapido(diasAtras(29), hoyISO())}>Últimos 30 días</button>
        </div>
        <div style={{flex:'1 1 140px', minWidth:140, maxWidth:180}}>
          <label className="form-label">Desde</label>
          <input type="date" className="form-input" value={desde} max={hoyISO()}
            onChange={e=>setDesde(e.target.value)} style={{width:'100%', fontSize:16}}/>
        </div>
        <div style={{flex:'1 1 140px', minWidth:140, maxWidth:180}}>
          <label className="form-label">Hasta</label>
          <input type="date" className="form-input" value={hasta} max={hoyISO()}
            onChange={e=>setHasta(e.target.value)} style={{width:'100%', fontSize:16}}/>
        </div>
        <div style={{flex:'1 1 180px', minWidth:180, maxWidth:240}}>
          <label className="form-label">Vendedor</label>
          <select className="form-input form-select" style={{width:'100%', fontSize:16}}
            value={cajeroFiltro} onChange={e=>{ setCajeroFiltro(e.target.value); setSelId(null) }}>
            <option value="">Todos los vendedores</option>
            {cajeros.map(([id,nombre]) => <option key={id} value={id}>{nombre}</option>)}
          </select>
        </div>
      </div>

      {cargando ? (
        <div className="page-loading" style={{height:200}}><div className="spinner"/><span>Cargando cierres...</span></div>
      ) : !sel ? (
        <div className="card">
          <div className="empty-state" style={{padding:40}}>
            <p>No hay cierres de caja en este período</p>
            <p style={{fontSize:12, color:'var(--color-hint)', marginTop:6}}>Prueba con otro rango de fechas</p>
          </div>
        </div>
      ) : (
        <>
          {/* ═══ CIERRE DESTACADO ═══ */}
          <div className="card" style={{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12}}>
            <div>
              <div style={{fontWeight:700, fontSize:16}}>Cierre de {sel.cajero} · {fmtFecha(sel.cierre_en)}</div>
              <div style={{fontSize:13, color:'var(--color-muted)'}}>
                {sel.caja_nombre} · Abrió {fmtHora(sel.apertura_en)} · Cerró {fmtHora(sel.cierre_en)}
              </div>
            </div>
            <div style={{textAlign:'right', background:'var(--color-success-bg)', borderRadius:10, padding:'10px 16px'}}>
              <div style={{fontSize:12, color:'var(--color-muted)'}}>Total vendido por el vendedor</div>
              <div style={{fontSize:22, fontWeight:800, color:'var(--color-success)'}}>{fmt(sel.total_ventas)} ↗</div>
              <div style={{fontSize:11, color:'var(--color-muted)'}}>🧾 {nDocs(sel)} documentos · {sel.n_transacciones} transacciones</div>
            </div>
          </div>

          {/* Tarjetas KPI */}
          <div className="metric-grid">
            <div className="metric-card">
              <div className="metric-label">🧾 Documentos emitidos</div>
              <div className="metric-value">{nDocs(sel)}</div>
              <button className="btn btn-secondary" style={{marginTop:6, padding:'5px 10px', fontSize:12}}
                onClick={()=>verDocumentos(sel)}>Ver documentos ≡</button>
            </div>
            <div className="metric-card">
              <div className="metric-label">✅ Cierre teórico (sistema)</div>
              <div className="metric-value">{fmt(totTeorico(sel))}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">🧮 Cierre entregado por el vendedor</div>
              <div className="metric-value">{fmt(totEntregado(sel))}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">{difSel === 0 ? '✓ Diferencia' : '⚠ Diferencia'}</div>
              <div className="metric-value" style={{color: colDif(difSel)}}>{difSel === 0 ? '$0' : fmtDif(difSel)}</div>
              <div className="metric-sub">{difSel === 0 ? 'Cierre cuadrado' : difSel < 0 ? 'Falta dinero' : 'Sobra dinero'}</div>
            </div>
          </div>

          {/* Comparación + Resumen de documentos */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(340px, 1fr))', gap:16, alignItems:'start'}}>

            {/* Comparación de cierre */}
            <div className="card">
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center',
                gap:10, marginBottom:12, flexWrap:'wrap'}}>
                <div style={{fontWeight:700, fontSize:15}}>Comparación de cierre de caja</div>
                {esAdmin && (
                  <button className="btn btn-secondary" style={{padding:'6px 12px', fontSize:12, whiteSpace:'nowrap'}}
                    onClick={()=>abrirCorreccion(sel)}>
                    ✎ Corregir cierre
                  </button>
                )}
              </div>
              <div style={{overflowX:'auto', WebkitOverflowScrolling:'touch'}}>
              <table style={{width:'100%', minWidth:340, fontSize:13, borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{color:'var(--color-muted)', fontSize:11, textTransform:'uppercase', letterSpacing:'.03em'}}>
                    <th style={{textAlign:'left', paddingBottom:8, fontWeight:600}}>Concepto</th>
                    <th style={{textAlign:'right', paddingBottom:8, fontWeight:600}}>Teórico (sistema)</th>
                    <th style={{textAlign:'right', paddingBottom:8, fontWeight:600}}>Entregado</th>
                    <th style={{textAlign:'right', paddingBottom:8, fontWeight:600}}>Diferencia</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{borderTop:'1px solid var(--color-border)'}}>
                    <td style={{padding:'8px 0'}}>Efectivo</td>
                    <td style={{textAlign:'right', color:'var(--color-muted)'}}>{fmt(sel.efectivo_teorico)}</td>
                    <td style={{textAlign:'right', fontWeight:500}}>{fmt(sel.efectivo_real)}</td>
                    <td style={{textAlign:'right', fontWeight:600, color:colDif(sel.efectivo_dif)}}>{sel.efectivo_dif === 0 ? '$0' : fmtDif(sel.efectivo_dif)}</td>
                  </tr>
                  <tr style={{borderTop:'1px solid var(--color-border)'}}>
                    <td style={{padding:'8px 0'}}>Tarjeta débito</td>
                    <td style={{textAlign:'right', color:'var(--color-muted)'}}>{fmt(sel.debito_teorico)}</td>
                    <td style={{textAlign:'right', fontWeight:500}}>{sel.debito_real === null ? '—' : fmt(sel.debito_real)}</td>
                    <td style={{textAlign:'right', fontWeight:600, color:colDif(sel.debito_dif)}}>{sel.debito_dif === 0 ? '$0' : fmtDif(sel.debito_dif)}</td>
                  </tr>
                  <tr style={{borderTop:'1px solid var(--color-border)'}}>
                    <td style={{padding:'8px 0'}}>Tarjeta crédito</td>
                    <td style={{textAlign:'right', color:'var(--color-muted)'}}>{fmt(sel.credito_teorico)}</td>
                    <td style={{textAlign:'right', fontWeight:500}}>{sel.credito_real === null ? '—' : fmt(sel.credito_real)}</td>
                    <td style={{textAlign:'right', fontWeight:600, color:colDif(sel.credito_dif)}}>{sel.credito_dif === 0 ? '$0' : fmtDif(sel.credito_dif)}</td>
                  </tr>
                  <tr style={{borderTop:'1px solid var(--color-border)'}}>
                    <td style={{padding:'8px 0'}}>Transferencias</td>
                    <td style={{textAlign:'right', color:'var(--color-muted)'}}>{fmt(sel.transferencia_teorico)}</td>
                    <td style={{textAlign:'right', fontWeight:500}}>{sel.transferencia_real === null ? '—' : fmt(sel.transferencia_real)}</td>
                    <td style={{textAlign:'right', fontWeight:600, color:colDif(sel.transferencia_dif)}}>{sel.transferencia_dif === 0 ? '$0' : fmtDif(sel.transferencia_dif)}</td>
                  </tr>
                  {(sel.otros_medios || []).map((m, i) => (
                    <tr key={i} style={{borderTop:'1px solid var(--color-border)'}}>
                      <td style={{padding:'8px 0'}}>{m.nombre}</td>
                      <td style={{textAlign:'right', color:'var(--color-muted)'}}>{fmt(m.total)}</td>
                      <td style={{textAlign:'right', fontWeight:500}}>{fmt(m.total)}</td>
                      <td style={{textAlign:'right', fontWeight:600, color:'var(--color-success)'}}>$0</td>
                    </tr>
                  ))}
                  <tr style={{borderTop:'2px solid var(--color-border)'}}>
                    <td style={{padding:'9px 0', fontWeight:700}}>Total</td>
                    <td style={{textAlign:'right', fontWeight:700}}>{fmt(totTeorico(sel))}</td>
                    <td style={{textAlign:'right', fontWeight:700}}>{fmt(totEntregado(sel))}</td>
                    <td style={{textAlign:'right', fontWeight:800, color:colDif(difSel)}}>{difSel === 0 ? '$0' : fmtDif(difSel)}</td>
                  </tr>
                </tbody>
              </table>
              </div>

              {/* Aviso de diferencia */}
              {difSel !== 0 ? (
                <div style={{marginTop:12, padding:'10px 14px', borderRadius:8,
                  background:'#FFF7E0', border:'1px solid #F0D48A'}}>
                  <div style={{fontWeight:700, fontSize:13, color:'#8A6100'}}>⚠ Diferencia encontrada</div>
                  <div style={{fontSize:12, color:'#8A6100', marginTop:2}}>
                    {difSel < 0 ? `Faltan ${fmt(Math.abs(difSel))}` : `Sobran ${fmt(difSel)}`} entre el cierre teórico y el entregado por el vendedor.
                    Revisa los documentos y movimientos para más información.
                  </div>
                </div>
              ) : (
                <div style={{marginTop:12, padding:'10px 14px', borderRadius:8,
                  background:'var(--color-success-bg)'}}>
                  <div style={{fontWeight:700, fontSize:13, color:'var(--color-success)'}}>✓ Cierre cuadrado</div>
                  <div style={{fontSize:12, color:'var(--color-muted)', marginTop:2}}>
                    Todo lo entregado coincide con lo que calculó el sistema. ¡Excelente!
                  </div>
                </div>
              )}
            </div>

            {/* Resumen de documentos */}
            <div className="card">
              <div style={{fontWeight:700, fontSize:15, marginBottom:12}}>Resumen de documentos</div>
              {donaData.length === 0 ? (
                <div className="empty-state" style={{padding:30}}><p>Sin documentos en este turno</p></div>
              ) : (
                <div style={{display:'flex', alignItems:'center', gap:20, flexWrap:'wrap'}}>
                  <Dona data={donaData}/>
                  <div style={{flex:1, minWidth:210}}>
                    {donaData.map((d, i) => (
                      <div key={i} style={{display:'flex', alignItems:'center', gap:8, fontSize:13, padding:'5px 0'}}>
                        <span style={{width:10, height:10, borderRadius:3, background:d.color, flexShrink:0}}/>
                        <span style={{flex:1}}>{d.label}</span>
                        <span style={{color:'var(--color-muted)', fontSize:12, width:62, textAlign:'right'}}>
                          {d.n} {d.n === 1 ? 'doc' : 'docs'}
                        </span>
                        <span style={{fontWeight:600, width:78, textAlign:'right'}}>{fmt(d.total)}</span>
                      </div>
                    ))}
                    <div style={{display:'flex', alignItems:'center', gap:8, fontSize:13, padding:'7px 0 0',
                      marginTop:4, borderTop:'1px solid var(--color-border)', fontWeight:700}}>
                      <span style={{flex:1}}>Total</span>
                      <span style={{color:'var(--color-muted)', fontSize:12, width:62, textAlign:'right'}}>
                        {totalDocsSel} {totalDocsSel === 1 ? 'doc' : 'docs'}
                      </span>
                      <span style={{width:78, textAlign:'right'}}>{fmt(totalMontoSel)}</span>
                    </div>
                    {(sel.documentos || []).some(d => d.n_anulados > 0) && (
                      <div style={{fontSize:12, color:'var(--color-danger)', marginTop:6}}>
                        ✕ Anuladas: {(sel.documentos || []).reduce((s, d) => s + d.n_anulados, 0)} (no se cuentan en los totales)
                      </div>
                    )}
                  </div>
                </div>
              )}
              <button className="btn btn-secondary btn-block" style={{marginTop:12}}
                onClick={()=>verDocumentos(sel)}>🧾 Ver todos los documentos</button>
            </div>
          </div>

          {/* Efectivo + Movimientos */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:16, alignItems:'start'}}>
            <div className="card">
              <div style={{fontWeight:700, fontSize:14, marginBottom:8}}>💵 ¿De dónde sale el efectivo?</div>
              <FilaEfectivo label={`Abrió a las ${fmtHora(sel.apertura_en)} con`} valor={sel.monto_inicial} signo="+"/>
              <FilaEfectivo label="Ventas en efectivo" valor={sel.efectivo_ventas} signo="+"/>
              <FilaEfectivo label="Ingresos manuales" valor={ingresosSel} signo="+"/>
              <FilaEfectivo label="Retiros y gastos" valor={egresosSel} signo="−"/>
              <FilaEfectivo label="Fondo para la siguiente apertura" valor={sel.fondo_siguiente} signo="−"/>
              <div style={{display:'flex', justifyContent:'space-between', fontSize:13, padding:'6px 0 0', marginTop:4, borderTop:'1px solid var(--color-border)'}}>
                <span style={{fontWeight:700}}>Debería haber quedado</span>
                <span style={{fontWeight:800}}>{fmt(sel.efectivo_teorico)}</span>
              </div>
            </div>
            <div className="card">
              <div style={{fontWeight:700, fontSize:14, marginBottom:8}}>🔄 Ingresos y retiros de efectivo</div>
              {movsSinFondo.length === 0 ? (
                <div style={{fontSize:12, color:'var(--color-hint)', fontStyle:'italic'}}>No hubo movimientos manuales en este turno</div>
              ) : movsSinFondo.map((m, i) => (
                <div key={i} style={{display:'flex', justifyContent:'space-between', gap:8, fontSize:13, padding:'3px 0'}}>
                  <span style={{color:'var(--color-muted)'}}>
                    {m.tipo === 'ingreso' ? '↓' : '↑'} {m.concepto}
                    <span style={{fontSize:11, color:'var(--color-hint)'}}> · {fmtHora(m.creado_en)} · {m.usuario}</span>
                  </span>
                  <span style={{fontWeight:600, whiteSpace:'nowrap',
                    color: m.tipo === 'ingreso' ? 'var(--color-success)' : 'var(--color-danger)'}}>
                    {m.tipo === 'ingreso' ? '+' : '−'}{fmt(m.monto)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Nota, corrección */}
          <div className="card" style={{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10}}>
            <div style={{fontSize:12, color:'var(--color-muted)'}}>
              {sel.observacion && <div>Nota: {sel.observacion}</div>}
              {sel.cierre_automatico && (
                <div style={{fontWeight:600, color:'#B8860B'}}>
                  ⏰ Esta caja fue cerrada automáticamente por el sistema (quedó abierta sin actividad desde un día anterior). Nadie contó el efectivo, por eso no hay diferencia real que revisar.
                </div>
              )}
              {sel.corregido_en && (
                <div>✎ Este cierre fue corregido por {sel.corregido_por_nombre || 'un administrador'} el {fmtFecha(sel.corregido_en)} a las {fmtHora(sel.corregido_en)}</div>
              )}
              {!sel.observacion && !sel.corregido_en && <span>Cierre registrado por {sel.cajero}</span>}
            </div>
          </div>

          {/* ═══ HISTORIAL DE CIERRES ═══ */}
          <div className="card">
            <div style={{fontWeight:700, fontSize:15, marginBottom:12}}>
              Historial de cierres {cajeroFiltro ? 'del vendedor' : ''} · {visibles.length} {visibles.length === 1 ? 'cierre' : 'cierres'}
            </div>
            <div className="table-wrap" style={{overflowX:'auto', WebkitOverflowScrolling:'touch'}}>
              <table style={{minWidth:680}}>
                <thead>
                  <tr>
                    <th>Fecha</th><th>Caja</th><th>Vendedor</th>
                    <th style={{textAlign:'right'}}>Total teórico</th>
                    <th style={{textAlign:'right'}}>Total entregado</th>
                    <th style={{textAlign:'right'}}>Diferencia</th>
                    <th style={{textAlign:'right'}}>Documentos</th>
                    <th style={{textAlign:'right'}}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map(t => (
                    <tr key={t.id} style={{background: t.id === sel.id ? 'var(--color-bg)' : 'transparent'}}>
                      <td>{fmtFecha(t.cierre_en)} <span style={{color:'var(--color-hint)', fontSize:11}}>{fmtHora(t.cierre_en)}</span></td>
                      <td style={{color:'var(--color-muted)'}}>{t.caja_nombre}</td>
                      <td>
                        {esAdmin ? (
                          <button onClick={()=>{ setSelId(t.id); abrirCorreccion(t) }}
                            title="Abrir, revisar y corregir este cierre"
                            style={{background:'none', border:'none', padding:0, margin:0, cursor:'pointer',
                              color:'var(--color-primary)', textDecoration:'underline', font:'inherit'}}>
                            {t.cajero}
                          </button>
                        ) : t.cajero}
                        {t.cierre_automatico && <span title="Cerrada automáticamente por el sistema" style={{marginLeft:6}}>⏰</span>}
                      </td>
                      <td style={{textAlign:'right'}}>{fmt(totTeorico(t))}</td>
                      <td style={{textAlign:'right', fontWeight:500}}>{fmt(totEntregado(t))}</td>
                      <td style={{textAlign:'right', fontWeight:700, color:colDif(difTotal(t))}}>
                        {difTotal(t) === 0 ? '$0 ✓' : fmtDif(difTotal(t))}
                      </td>
                      <td style={{textAlign:'right'}}>{nDocs(t)}</td>
                      <td style={{textAlign:'right', whiteSpace:'nowrap'}}>
                        <button className="btn btn-secondary" style={{padding:'4px 9px', fontSize:12, marginRight:6}}
                          title="Ver este cierre arriba" onClick={()=>{ setSelId(t.id); window.scrollTo({top:0, behavior:'smooth'}) }}>👁</button>
                        <button className="btn btn-secondary" style={{padding:'4px 9px', fontSize:12}}
                          title="Ver documentos del turno" onClick={()=>verDocumentos(t)}>🧾</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ═══ MODAL: Documentos del turno ═══ */}
      {docsModal && !reimpresion && (
        <div onMouseDown={cerrarSiEsFondo(() => setDocsModal(null))}
          style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000,
            display:'flex', alignItems:'center', justifyContent:'center', padding:16}}>
          <div className="card"
            style={{width:'100%', maxWidth:620, maxHeight:'88vh', display:'flex', flexDirection:'column'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
              <div>
                <div className="section-title" style={{marginBottom:0}}>Documentos del turno</div>
                <div style={{fontSize:12, color:'var(--color-muted)'}}>
                  {docsModal.cajero} · {docsModal.caja_nombre} · {fmtFecha(docsModal.cierre_en)}
                </div>
              </div>
              <button onClick={()=>setDocsModal(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'var(--color-muted)'}}>✕</button>
            </div>
            {cargandoDocs ? (
              <div className="page-loading" style={{height:120}}><div className="spinner"/></div>
            ) : docsLista.length === 0 ? (
              <div className="empty-state" style={{padding:30}}><p>No hay documentos en este turno</p></div>
            ) : (
              <div className="table-wrap" style={{overflowY:'auto'}}>
                <table>
                  <thead><tr><th>Hora</th><th>N°</th><th>Tipo</th><th style={{textAlign:'right'}}>Total</th><th>Estado</th><th></th></tr></thead>
                  <tbody>
                    {docsLista.map(v => (
                      <tr key={v.id}>
                        <td style={{color:'var(--color-muted)', fontSize:12}}>{fmtHora(v.creado_en)}</td>
                        <td style={{fontWeight:500}}>#{v.folio || v.id.slice(-6).toUpperCase()}</td>
                        <td style={{color:'var(--color-muted)'}}>{nombreDoc(v.tipo).replace(/s$/,'')}</td>
                        <td style={{textAlign:'right', fontWeight:600}}>{fmt(v.total)}</td>
                        <td><span className={`badge ${v.estado==='anulado'?'badge-danger':'badge-success'}`}>{v.estado==='anulado'?'Anulada':'Emitida'}</span></td>
                        <td style={{textAlign:'right'}}>
                          {v.estado !== 'anulado' && (
                            <button className="btn btn-secondary" style={{padding:'4px 9px', fontSize:12}}
                              disabled={cargandoReimp === v.id} onClick={()=>reimprimir(v)}>
                              {cargandoReimp === v.id ? '...' : '🖨️'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ MODAL: Reimpresión de boleta ═══ */}
      {reimpresion && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,.6)',
          display:'flex', alignItems:'center', justifyContent:'center',
          zIndex:2000, padding:20
        }}>
          <div className="card" style={{
            width:'100%', maxWidth:420, maxHeight:'92vh',
            display:'flex', flexDirection:'column', padding:0, overflow:'hidden'
          }}>
            <div style={{
              padding:'16px 20px', borderBottom:'1px solid var(--color-border)',
              display:'flex', justifyContent:'space-between', alignItems:'center'
            }}>
              <div>
                <div style={{fontWeight:700, fontSize:15}}>🖨️ Reimprimir documento</div>
                <div style={{fontSize:12, color:'var(--color-muted)'}}>
                  Boleta #{reimpresion.documento.folio || reimpresion.documento.id?.slice(-6).toUpperCase()}
                </div>
              </div>
              <button onClick={()=>setReimpresion(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'var(--color-muted)'}}>✕</button>
            </div>
            <div style={{flex:1, overflowY:'auto', padding:'16px', background:'#f0f0ee'}}>
              <div style={{background:'white', boxShadow:'0 2px 8px rgba(0,0,0,.1)', display:'inline-block', width:'100%'}}>
                <Boleta venta={reimpresion} usuario={usuario}/>
              </div>
            </div>
            <div style={{padding:'16px 20px', borderTop:'1px solid var(--color-border)', display:'flex', flexDirection:'column', gap:8}}>
              <button className="btn btn-primary btn-block" onClick={()=>window.print()}>🖨️ Imprimir boleta</button>
              <button className="btn btn-secondary btn-block" onClick={descargarPdfReimp} disabled={generandoPdf}>
                {generandoPdf ? 'Generando PDF...' : '📥 Descargar PDF'}
              </button>
              <button className="btn btn-secondary btn-block" onClick={()=>setReimpresion(null)} style={{color:'var(--color-muted)'}}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: Corregir cierre (solo admin) ═══ */}
      {corrigiendo && (
        <div onMouseDown={cerrarSiEsFondo(() => !guardando && setCorrigiendo(null))}
          style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1500,
            display:'flex', alignItems:'center', justifyContent:'center', padding:16}}>
          <div className="card"
            style={{width:'100%', maxWidth:440, maxHeight:'90vh', overflowY:'auto'}}>

            <div className="section-title" style={{marginBottom:2}}>Corregir cierre</div>
            <div style={{fontSize:12, color:'var(--color-muted)', marginBottom:14}}>
              {corrigiendo.cajero} · {corrigiendo.caja_nombre} · {fmtFecha(corrigiendo.cierre_en)}
            </div>

            <div className="form-group">
              <label className="form-label">Efectivo contado</label>
              <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                value={formCorr.efectivo}
                onChange={e=>setFormCorr({...formCorr, efectivo: e.target.value.replace(/[^0-9.,]/g,'')})}/>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
              <div className="form-group" style={{marginBottom:0}}>
                <label className="form-label">Débito (según máquina)</label>
                <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                  value={formCorr.debito}
                  onChange={e=>setFormCorr({...formCorr, debito: e.target.value.replace(/[^0-9.,]/g,'')})}/>
              </div>
              <div className="form-group" style={{marginBottom:0}}>
                <label className="form-label">Crédito (según máquina)</label>
                <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                  value={formCorr.credito}
                  onChange={e=>setFormCorr({...formCorr, credito: e.target.value.replace(/[^0-9.,]/g,'')})}/>
              </div>
            </div>

            <div className="form-group" style={{marginTop:12}}>
              <label className="form-label">Transferencias recibidas</label>
              <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                value={formCorr.transferencia}
                onChange={e=>setFormCorr({...formCorr, transferencia: e.target.value.replace(/[^0-9.,]/g,'')})}/>
            </div>

            <div className="form-group">
              <label className="form-label">Fondo dejado para la siguiente apertura</label>
              <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                value={formCorr.fondo}
                onChange={e=>setFormCorr({...formCorr, fondo: e.target.value.replace(/[^0-9.,]/g,'')})}/>
              <div style={{fontSize:11, color:'var(--color-muted)', marginTop:4}}>
                Si lo cambias, el efectivo teórico se recalcula automáticamente.
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Motivo de la corrección (opcional)</label>
              <input className="form-input" placeholder="ej: El vendedor tipeó mal el papelito de débito"
                value={formCorr.observacion}
                onChange={e=>setFormCorr({...formCorr, observacion: e.target.value})}/>
            </div>

            <div style={{display:'flex', gap:10, marginTop:6}}>
              <button className="btn btn-secondary" style={{flex:1}} disabled={guardando}
                onClick={()=>setCorrigiendo(null)}>Cancelar</button>
              <button className="btn btn-primary" style={{flex:1}} disabled={guardando}
                onClick={guardarCorreccion}>{guardando ? 'Guardando...' : 'Guardar corrección'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
